import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button, Grid, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'

import BackButton from '../../../components/back-button'
import ScreenContainer from '../../../components/screen'
import SensorActivationFooter from '../../../components/sensor-activation-footer'
import activatedSlimSensor from '../../../images/activated-slim.png'
import { useActivationContext } from '../../../providers/activation-provider'
import Api from '../../../utils/api'

import useStyles from './index.styles'

const CompleteSetupLeakFreeze = ({ node }: { node: INodeType | undefined }) => {
  const classes = useStyles
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { leakFreezeNotInstalled, activationLocationState, getDeviceActivationCheck, loading } = useActivationContext()
  const { folderId } = activationLocationState

  const [submitting, setSubmitting] = useState<boolean>(false)

  // Update the leakFreezeNotInstalled count on load so we show the right button
  useEffect(() => {
    getDeviceActivationCheck()
  }, [getDeviceActivationCheck])

  const onCompleteActivation = async () => {
    if (!folderId) return
    setSubmitting(true)

    if (leakFreezeNotInstalled > 0) {
      await getDeviceActivationCheck()
      navigate(`/device-roster/${folderId}`)
      setSubmitting(false)
    } else {
      try {
        const resActivation = await Api.post(`/api/v2/folders/${folderId}/activation`, {})
        const resAttributes = await Api.patch(`/api/v2/folders/${folderId}/attributes`, {
          activationMode: 'online',
          activationApp: 'Protect Web',
        })
        if (!resActivation.ok || !resAttributes.ok) throw new Error() // Throw error to the catch block
        await getDeviceActivationCheck()
        setSubmitting(false)
        enqueueSnackbar('Installation completed successfully')
        navigate(`/dashboard/${folderId}`, { state: { showBookmarkInfo: true } })
      } catch {
        setSubmitting(false)
        enqueueSnackbar('Could not complete installation, try again', { variant: 'error' })
      }
    }
  }

  return (
    <ScreenContainer
      loading={!node || loading}
      headerProps={{
        title: 'Activate',
        showDownloadAppButton: false,
        leftIcon: <BackButton />,
      }}
    >
      <Grid container={true} sx={classes.mainContainer}>
        <Typography variant="h4" sx={classes.titleText}>
          Great work
        </Typography>

        <img src={activatedSlimSensor} alt="Activated Leak & Freeze Sensor" />

        <Typography variant="h5" sx={classes.activatedText}>
          Your sensor is activated!
        </Typography>

        <Grid container={true} size={12} sx={classes.leakFreezeLocationInformation}>
          <Typography variant="body2" color="tertiary.main" sx={classes.locationLabel}>
            Location:
          </Typography>

          <Typography variant="body1" color="common.black" sx={classes.locationValue}>
            {node?.area}
            {node?.area && node?.metadata?.device_location_note && ' - '}
            {node?.metadata?.device_location_note}
            <br />
            {node?.floor && `Floor ${node.floor}`}
          </Typography>
        </Grid>

        <SensorActivationFooter
          useLightBackground={true}
          primaryButton={
            <Button variant="contained" disableElevation loading={submitting} disabled={submitting} onClick={onCompleteActivation}>
              {leakFreezeNotInstalled > 0 ? 'Activate more' : 'Complete Installation'}
            </Button>
          }
          sxFooter={classes.leakFreezeFooter}
        />
      </Grid>
    </ScreenContainer>
  )
}

export default CompleteSetupLeakFreeze
